/**
 * src/data/MeetingHeroBank.js
 * Hardcoded scenario bank for Part 5: Meeting Hero
 */

export const meetingHeroBank = [
  {
    "id": "meeting_01",
    "meetingType": "Q3 Strategy Wrap-Up",
    "leaderPrompt": "TEAM LEAD: 'Okay, that's the high-level roadmap for Q3. I need a go/no-go in the chat from everyone before we adjourn.'",
    "options": [
      {
        "text": "Go. Does this proposed delivery window impact our Q4 resource budget?",
        "category": "Optimal",
        "feedback": "Good catch. Adds clear strategic value and shows active listening while fulfilling the prompt's request."
      },
      {
        "text": "Great strategy! I agree with everything! Go team!",
        "category": "Suboptimal",
        "feedback": "Adds unnecessary noise to the chat without contributing operational insight."
      },
      {
        "text": "No-go. I don't think this strategy will work at all.",
        "category": "Disruptive",
        "feedback": "Destructive feedback without data or alternative solutions derails momentum."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "In a remote environment, remaining silent during a direct call for input renders you invisible and halts consensus."
      }
    ]
  },
  {
    "id": "meeting_02",
    "meetingType": "Software Demo",
    "leaderPrompt": "PRESENTER: [Screen freezes completely while demonstrating the new checkout flow] 'As you can clearly see here on the dashboard, the user clicks next...'",
    "options": [
      {
        "text": "Hey [Name], your screen seems frozen on the login page. Should we pause while you reconnect?",
        "category": "Optimal",
        "feedback": "Proactive and helpful. Saves the presenter from wasting time and resets the meeting efficiently."
      },
      {
        "text": "Wow, tech issues today! Mondays, right?",
        "category": "Suboptimal",
        "feedback": "Fills the chat with irrelevant commentary without actually alerting the presenter to the specific issue."
      },
      {
        "text": "This is a waste of time, let's just reschedule.",
        "category": "Disruptive",
        "feedback": "Hostile and impatient. Escalates a minor technical glitch into a needlessly negative confrontation."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Bystander effect. Letting a presenter talk to a frozen screen wastes company time and creates profound awkwardness."
      }
    ]
  },
  {
    "id": "meeting_03",
    "meetingType": "Annual Budget Review",
    "leaderPrompt": "FINANCE LEAD: 'We need to cut 10% from departmental expenses. I need everyone in the chat to confirm they received the worksheet and state their initial thoughts.'",
    "options": [
      {
        "text": "Received. Is there a priority matrix for which vendor contracts we should review for cuts first?",
        "category": "Optimal",
        "feedback": "Acknowledges the directive while asking a highly relevant operational question to guide the team's efforts."
      },
      {
        "text": "Got it! Thanks for sending, looking forward to it!",
        "category": "Suboptimal",
        "feedback": "Toxic positivity. Treats a serious budget cut with jarring enthusiasm and adds no strategic value."
      },
      {
        "text": "There's no way my department can cut 10%, this is a ridiculous ask.",
        "category": "Disruptive",
        "feedback": "Displays insubordination and panic in a public forum, instantly derailing the planning session."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to confirm receipt of critical financial documents when explicitly asked shows poor remote compliance."
      }
    ]
  },
  {
    "id": "meeting_04",
    "meetingType": "Mid-Sprint Check-In",
    "leaderPrompt": "PRODUCT MANAGER: 'Sales is asking if we can just squeeze in the new reporting export for Friday's release. I need a vote in the chat from the devs: yes or no?'",
    "options": [
      {
        "text": "If we add the export, which current ticket are we deprioritizing to maintain our velocity?",
        "category": "Optimal",
        "feedback": "Excellent boundary-setting. Addresses the scope creep by forcing a realistic conversation about resource tradeoffs."
      },
      {
        "text": "Whatever the team wants to do is fine by me!",
        "category": "Suboptimal",
        "feedback": "Abdicates responsibility. Fails to provide the requested vote or useful engineering insight."
      },
      {
        "text": "Sales always does this. Tell them no and tell them to stop bothering us.",
        "category": "Disruptive",
        "feedback": "Highly unprofessional cross-departmental hostility. Creates an adversarial culture."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Refusing to vote when capacity is at stake forces the PM to make decisions without technical consensus."
      }
    ]
  },
  {
    "id": "meeting_05",
    "meetingType": "Security Risk Assessment",
    "leaderPrompt": "COLLEAGUE: 'Before we look at the firewall audit, why hasn't HR responded about the holiday party dates? We need to figure that out right now.'",
    "options": [
      {
        "text": "Can we table the holiday party for Slack and refocus on the firewall vulnerabilities we need to clear today?",
        "category": "Optimal",
        "feedback": "Politely but firmly intercepts the hijacked agenda and redirects focus back to the critical meeting objective."
      },
      {
        "text": "Yeah, I love the holiday parties! I hope it's on a Friday.",
        "category": "Suboptimal",
        "feedback": "Encourages the derailment. Contributes to the loss of focus during a high-stakes security meeting."
      },
      {
        "text": "I swear, nobody in this company cares about security. You guys are incompetent.",
        "category": "Disruptive",
        "feedback": "Extremely toxic and insulting. Turns a simple distraction into a hostile HR incident."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Passively allowing a colleague to hijack a critical security meeting shows a lack of leadership and time management."
      }
    ]
  },
  {
    "id": "meeting_06",
    "meetingType": "All-Hands Town Hall",
    "leaderPrompt": "CEO: [Mouth is moving enthusiastically for 30 seconds, but the microphone is clearly muted. No one has interrupted yet.]",
    "options": [
      {
        "text": "@[CEO Name] We seem to have lost your audio! Could you check your mic settings?",
        "category": "Optimal",
        "feedback": "Demonstrates courage and basic courtesy by stopping the presentation before more time is wasted."
      },
      {
        "text": "Looking good everyone! Happy Friday!",
        "category": "Suboptimal",
        "feedback": "Bizarrely ignores the massive technical failure happening on screen to send empty platitudes."
      },
      {
        "text": "Is anyone going to tell him he's talking to himself? What a joke.",
        "category": "Disruptive",
        "feedback": "Passive-aggressive and disrespectful. Complaining in the chat instead of simply helping fix the issue."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Succumbing to the bystander effect allows the CEO to continue wasting their time and creates a highly awkward situation."
      }
    ]
  },
  {
    "id": "meeting_07",
    "meetingType": "Design vs. Dev Sync",
    "leaderPrompt": "LEAD DESIGNER: 'The new UI animations are non-negotiable for the brand. Developers, do you foresee any blockers? Type yes/no or any questions in chat.'",
    "options": [
      {
        "text": "Could we get the specific performance metrics required so we can test if these animations impact mobile load times?",
        "category": "Optimal",
        "feedback": "Translates a rigid demand into a collaborative, data-driven engineering question."
      },
      {
        "text": "The designs look so pretty, amazing work!",
        "category": "Suboptimal",
        "feedback": "Flattery that completely ignores the prompt asking for technical blockers."
      },
      {
        "text": "Devs don't have time for your vanity animations, cut them from the scope.",
        "category": "Disruptive",
        "feedback": "Combative and dismissive. Instantly creates a toxic silo between Design and Engineering."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to flag potential technical blockers when directly asked guarantees friction during the development phase."
      }
    ]
  },
  {
    "id": "meeting_08",
    "meetingType": "Crisis Management - P0 Outage",
    "leaderPrompt": "VP OF ENGINEERING: 'The main production database is down. I need a roll-call right now of who is available to troubleshoot.'",
    "options": [
      {
        "text": "Available. Should I start by pulling the recent deployment logs or checking the server memory spikes?",
        "category": "Optimal",
        "feedback": "Perfect crisis response. Confirms availability and immediately offers actionable, specific diagnostic help."
      },
      {
        "text": "Here and ready to help, team! We got this!",
        "category": "Suboptimal",
        "feedback": "Cheerleading during a P0 outage clutters the chat when leaders need concise, technical operational readiness."
      },
      {
        "text": "I told you all this migration was a terrible idea. This is what happens.",
        "category": "Disruptive",
        "feedback": "Unhelpful 'I told you so' behavior. Wastes critical recovery time on placing blame rather than fixing the outage."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Ignoring a direct roll-call from a VP during a critical outage suggests panic, absence, or negligence."
      }
    ]
  },
  {
    "id": "meeting_09",
    "meetingType": "Weekly Marketing Sync",
    "leaderPrompt": "MARKETING DIRECTOR: 'So moving on to the... [Loud, painful audio screeching echo fills the call]... Can anyone hear me?'",
    "options": [
      {
        "text": "We're getting a severe echo. Could everyone except the presenter mute their mics to isolate the issue?",
        "category": "Optimal",
        "feedback": "Identifies the problem and instantly provides a standard operational solution to fix it."
      },
      {
        "text": "Ouch, my ears! Haha!",
        "category": "Suboptimal",
        "feedback": "Treats a disruptive technical issue as a joke, adding noise to the chat without helping."
      },
      {
        "text": "Fix your mics people, this is a professional environment, not a rock concert.",
        "category": "Disruptive",
        "feedback": "Arrogant and antagonistic. Alienates colleagues over a common software audio glitch."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to answer the presenter's direct question leaves them stranded and prolongs the meeting disruption."
      }
    ]
  },
  {
    "id": "meeting_10",
    "meetingType": "Client Onboarding Call",
    "leaderPrompt": "CLIENT: 'Since we're paying for the premium tier, we'd also like your team to manage our social media accounts. Can you handle that?'",
    "options": [
      {
        "text": "That falls outside standard premium support, but I can connect you with our Account Manager to discuss an add-on package?",
        "category": "Optimal",
        "feedback": "Politely defends project scope while offering a constructive, revenue-generating pathway for the client."
      },
      {
        "text": "You're going to love our premium tier!",
        "category": "Suboptimal",
        "feedback": "Completely ignores the client's scope-creep request, creating confusion about what was actually agreed upon."
      },
      {
        "text": "No, read your contract. We don't do social media.",
        "category": "Disruptive",
        "feedback": "Aggressive and legally blunt. Damages the client relationship immediately during onboarding."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Silence in the face of scope creep is often interpreted by clients as consent, setting the team up for failure."
      }
    ]
  },
  {
    "id": "meeting_11",
    "meetingType": "Project Alpha Kickoff",
    "leaderPrompt": "PROJECT LEAD: 'Welcome to Alpha. First agenda item is timelines.' \nCOLLEAGUE: 'Wait, I want to talk about why Project Beta failed last month. We need answers.'",
    "options": [
      {
        "text": "Since we only have 30 minutes for Alpha's timeline, could we schedule a separate post-mortem for Beta?",
        "category": "Optimal",
        "feedback": "De-escalates the tension and protects the current agenda by offering a dedicated space for the colleague's concerns."
      },
      {
        "text": "Ooh, Beta was a tough one for sure!",
        "category": "Suboptimal",
        "feedback": "Validates the derailment and encourages off-topic gossip rather than protecting the meeting's purpose."
      },
      {
        "text": "Can we shut up about Beta already? I'm sick of hearing about it.",
        "category": "Disruptive",
        "feedback": "Highly confrontational. Turns a meeting derailment into a full-blown interpersonal conflict."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to assist the Project Lead in managing a disruptive colleague allows the meeting to descend into chaos."
      }
    ]
  },
  {
    "id": "meeting_12",
    "meetingType": "Executive Briefing",
    "leaderPrompt": "DIRECTOR: 'Please confirm in chat if we are clear to proceed to slide 5.' [Director immediately switches to slide 5 without pausing].",
    "options": [
      {
        "text": "Quick question on Slide 4 before we get too deep into Slide 5: what was the source for those Q2 metrics?",
        "category": "Optimal",
        "feedback": "Politely interrupts the rushed pacing to extract critical missing context without undermining the speaker."
      },
      {
        "text": "Great slide!",
        "category": "Suboptimal",
        "feedback": "Meaningless filler text that ignores the fact that the presenter is moving too fast for actual comprehension."
      },
      {
        "text": "You're going way too fast, go back. We can't read that.",
        "category": "Disruptive",
        "feedback": "Rude and commanding tone toward a superior. Expresses frustration rather than professional curiosity."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to confirm or object when explicitly asked allows the presenter to gloss over vital information unchecked."
      }
    ]
  },
  {
    "id": "meeting_13",
    "meetingType": "Cross-Department Resource Planning",
    "leaderPrompt": "OPERATIONS LEAD: 'We only have budget for one new hire this quarter. Both IT and HR have requested headcount. I need each department's stance on this in the chat.'",
    "options": [
      {
        "text": "Can we review the ROI projections for both roles to determine which solves a more immediate operational bottleneck?",
        "category": "Optimal",
        "feedback": "Elevates a potential turf war into an objective, data-driven business discussion."
      },
      {
        "text": "I love both teams, so hard to choose!",
        "category": "Suboptimal",
        "feedback": "Childish avoidance of a tough business decision. Adds absolutely no value to the resource debate."
      },
      {
        "text": "HR doesn't do anything anyway, IT obviously needs it more.",
        "category": "Disruptive",
        "feedback": "Extremely unprofessional and disparaging toward another department. Creates toxic company culture."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Remaining silent when specifically asked to advocate or provide input on departmental budgeting is a dereliction of duty."
      }
    ]
  },
  {
    "id": "meeting_14",
    "meetingType": "Training Webinar",
    "leaderPrompt": "TRAINER: 'Please click the link in the chat to download the mandatory compliance workbook and type 'Done' when finished.'",
    "options": [
      {
        "text": "The link seems to be throwing a 404 error. Do you have an alternate Google Drive link we can use?",
        "category": "Optimal",
        "feedback": "Identifies the technical roadblock immediately and suggests a practical workaround to keep the training moving."
      },
      {
        "text": "Clicking now, so excited for this training!",
        "category": "Suboptimal",
        "feedback": "Fake enthusiasm that fails to mention the link is actually broken, delaying the realization for everyone else."
      },
      {
        "text": "Typical IT, nothing ever works in this company. Fix the link.",
        "category": "Disruptive",
        "feedback": "Uses a minor broken link as an excuse to broadcast a toxic, pessimistic attitude to the entire training group."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Refusing to type 'Done' or alert the trainer to the broken link completely stalls the interactive webinar."
      }
    ]
  },
  {
    "id": "meeting_15",
    "meetingType": "Vendor Negotiation Debrief",
    "leaderPrompt": "PROCUREMENT: 'The vendor refused our 15% discount request. I need input from the stakeholders right now on whether we walk away or accept.'",
    "options": [
      {
        "text": "What is the hard cost of migrating to an alternative vendor compared to just accepting their current pricing?",
        "category": "Optimal",
        "feedback": "Frames the emotional negotiation decision around objective financial trade-offs and switching costs."
      },
      {
        "text": "Tough break, let's just do our best!",
        "category": "Suboptimal",
        "feedback": "Empty corporate jargon that completely fails to answer the critical 'walk away or accept' prompt."
      },
      {
        "text": "Procurement dropped the ball again. I could have negotiated a better deal myself.",
        "category": "Disruptive",
        "feedback": "Arrogant, insulting, and unhelpful. Destroys psychological safety and team trust."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "As a stakeholder, withholding input on a major vendor decision forces others to carry the risk and delays the contract."
      }
    ]
  },
  {
    "id": "meeting_16",
    "meetingType": "Incident Post-Mortem",
    "leaderPrompt": "SCRUM MASTER: 'Let's review the timeline of the outage.' \nENGINEER: 'It's entirely QA's fault for not catching the bug. They rushed it.'",
    "options": [
      {
        "text": "Let's focus on fixing the systemic process gap rather than individual blame. How did the bug bypass our automated tests?",
        "category": "Optimal",
        "feedback": "Outstanding leadership. Shuts down the toxic blame game and pivots the team toward constructive root-cause analysis."
      },
      {
        "text": "I'm sure everyone tried their hardest!",
        "category": "Suboptimal",
        "feedback": "Platitudes do not resolve the tension or stop the scapegoating occurring in the meeting."
      },
      {
        "text": "QA is completely useless, we might as well fire them if they can't catch this.",
        "category": "Disruptive",
        "feedback": "Joins the mob mentality. Highly destructive to team morale and grounds for HR intervention."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Remaining silent allows a teammate to be publicly scapegoated, fostering a culture of fear rather than learning."
      }
    ]
  },
  {
    "id": "meeting_17",
    "meetingType": "Legal Compliance Update",
    "leaderPrompt": "LEGAL COUNSEL: 'The new data privacy law takes effect tomorrow. I need chat confirmation from all managers that your teams are briefed.'",
    "options": [
      {
        "text": "Confirmed. Is there a standardized legal FAQ document we can provide to clients who ask about this transition?",
        "category": "Optimal",
        "feedback": "Provides the mandatory confirmation while proactively asking for resources to handle downstream client impact."
      },
      {
        "text": "Awesome presentation, learned a lot today!",
        "category": "Suboptimal",
        "feedback": "Fails to provide the legally required confirmation of team briefing, opting instead for generic praise."
      },
      {
        "text": "This law is stupid and is going to ruin our sales pipeline.",
        "category": "Disruptive",
        "feedback": "Venting frustration at Legal over external laws is unprofessional and wastes time."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to provide a legally mandated confirmation puts both the manager and the company at compliance risk."
      }
    ]
  },
  {
    "id": "meeting_18",
    "meetingType": "Client Status Sync",
    "leaderPrompt": "CLIENT: 'We know launch is next week, but we just realized we really need to add a small custom dashboard for the CEO. Can we sneak that in?'",
    "options": [
      {
        "text": "We can certainly scope that out. Would you prefer to push the launch date back, or allocate extra budget for a post-launch phase?",
        "category": "Optimal",
        "feedback": "Expertly handles scope creep. Maintains a positive relationship while instantly enforcing boundaries via trade-offs."
      },
      {
        "text": "We love your ideas, the CEO is going to be so happy!",
        "category": "Suboptimal",
        "feedback": "Blindly accepts scope creep, committing the team to unpaid crunch work and endangering the existing timeline."
      },
      {
        "text": "Absolutely not. The requirements were locked in weeks ago. Read the SOW.",
        "category": "Disruptive",
        "feedback": "Unnecessarily hostile. Destroys client rapport when a firm but diplomatic negotiation was required."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Silence equals consent to a client. Failing to speak up guarantees the team will be forced to absorb the extra work."
      }
    ]
  },
  {
    "id": "meeting_19",
    "meetingType": "Departmental Reorganization",
    "leaderPrompt": "VP OF OPERATIONS: [Audio is chopping violently, dropping every other word] 'So the new reporting structure... is going to... meaning that you will now report to...'",
    "options": [
      {
        "text": "@[VP Name], we are losing your audio completely! Would turning off your camera help stabilize your bandwidth?",
        "category": "Optimal",
        "feedback": "Tactful and actionable. Stops the delivery of critical information over a broken connection and provides a technical fix."
      },
      {
        "text": "So excited for the new changes! Love the new direction!",
        "category": "Suboptimal",
        "feedback": "Displays utter lack of situational awareness. Pretending everything is fine while missing critical structural updates."
      },
      {
        "text": "Your internet is terrible, nobody can hear a word you're saying.",
        "category": "Disruptive",
        "feedback": "Abrasive and disrespectful toward leadership. Diagnoses the problem but lacks basic professional courtesy."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Remaining silent ensures half the department will not know their new reporting structure, causing mass confusion post-meeting."
      }
    ]
  },
  {
    "id": "meeting_20",
    "meetingType": "Daily Agile Standup",
    "leaderPrompt": "DEVELOPER: '...anyway, the tactile feedback on these new blue switches on my mechanical keyboard is amazing. I spent all weekend customizing the keycaps.'",
    "options": [
      {
        "text": "Sounds like an awesome setup! Just to make sure we hit everyone's updates in the next 5 mins, did you have any blockers on the API ticket?",
        "category": "Optimal",
        "feedback": "Gracefully interrupts the hijacked agenda, validates the colleague briefly, and firmly pivots back to the meeting objective."
      },
      {
        "text": "I love blue switches! Where did you get the custom keycaps?",
        "category": "Suboptimal",
        "feedback": "Actively participates in the derailment, turning a quick daily standup into a casual hangout and wasting team time."
      },
      {
        "text": "Nobody cares about your keyboard, let's get back to work.",
        "category": "Disruptive",
        "feedback": "Aggressive and combative. Kills psychological safety and creates a hostile team environment."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to help the Scrum Master manage time allows one vocal individual to drain productivity from the entire team."
      }
    ]
  },
  {
    "id": "meeting_21",
    "meetingType": "Q4 Advertising Budget Approval",
    "leaderPrompt": "CMO: 'Even though our Q3 ads underperformed, I want to double the spend for Q4 to capture market share. I need a gut check from the room.'",
    "options": [
      {
        "text": "Before we double the spend, could we review the conversion funnel data to see exactly where the Q3 drop-off occurred?",
        "category": "Optimal",
        "feedback": "Challenges the risky proposal with a highly rational, data-driven operational request to mitigate financial risk."
      },
      {
        "text": "Love the bold move! Go big or go home!",
        "category": "Suboptimal",
        "feedback": "Enables reckless financial decision-making with empty hype rather than critical business thinking."
      },
      {
        "text": "That's a ridiculous idea. Throwing good money after bad is how companies go bankrupt.",
        "category": "Disruptive",
        "feedback": "Highly inflammatory. Attacks the CMO's competence publicly rather than debating the strategy professionally."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Withholding analytical pushback when asked for a 'gut check' on a major financial risk demonstrates weak leadership."
      }
    ]
  },
  {
    "id": "meeting_22",
    "meetingType": "Mandatory Platform Training",
    "leaderPrompt": "TRAINER: [Has been reading monotonously from slides for 15 minutes, completely ignoring three distinct questions typed into the chat by colleagues].",
    "options": [
      {
        "text": "Hi [Trainer], a few great questions popped up in the chat while you were presenting. Should we cover them now or hold them for a Q&A at the end?",
        "category": "Optimal",
        "feedback": "Politely forces the non-responsive presenter to acknowledge the audience without embarrassing them."
      },
      {
        "text": "Great slides, taking so many notes!",
        "category": "Suboptimal",
        "feedback": "Sycophantic filler that ignores the functional breakdown of the training session (ignored questions)."
      },
      {
        "text": "Are you ever going to check the chat? People are asking questions.",
        "category": "Disruptive",
        "feedback": "Hostile and aggressive tone that humiliates the presenter and makes the rest of the meeting incredibly tense."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to advocate for colleagues whose questions are being ignored allows the training to remain ineffective."
      }
    ]
  },
  {
    "id": "meeting_23",
    "meetingType": "Urgent Director Sync",
    "leaderPrompt": "DIRECTOR: 'The board needs a complete audit of our software licenses by 5 PM today. I know we're swamped, but I need a volunteer to own this.'",
    "options": [
      {
        "text": "I can own this. Which of my current afternoon deliverables should I deprioritize to ensure this gets done by 5 PM?",
        "category": "Optimal",
        "feedback": "Demonstrates high accountability by volunteering, while instantly establishing realistic boundaries for time management."
      },
      {
        "text": "Teamwork makes the dream work! We'll get through it!",
        "category": "Suboptimal",
        "feedback": "Toxic positivity. Spouts motivational jargon without actually stepping up to solve the immediate operational crisis."
      },
      {
        "text": "It's 2 PM. Giving us a massive audit with 3 hours' notice is completely unreasonable.",
        "category": "Disruptive",
        "feedback": "Venting frustration may feel justified, but it provides no solution to the immediate executive crisis."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Refusing to volunteer during an all-hands-on-deck crisis damages your reputation as a reliable team player."
      }
    ]
  },
  {
    "id": "meeting_24",
    "meetingType": "Global Alignment Sync",
    "leaderPrompt": "EMEA LEAD: 'We need to boil the ocean on this localized rollout to ensure we aren't selling ice to Eskimos. Do we all agree with that approach?'",
    "options": [
      {
        "text": "Could you clarify the specific success metrics you want to see for the rollout? Just want to make sure the US team is aligned.",
        "category": "Optimal",
        "feedback": "Deftly cuts through confusing cultural idioms and business jargon to request concrete, actionable operational metrics."
      },
      {
        "text": "Totally agree! Great approach!",
        "category": "Suboptimal",
        "feedback": "Blindly agreeing to a highly confusing, jargon-filled statement guarantees misalignment across global teams."
      },
      {
        "text": "Nobody understands those idioms. Can you just speak plainly?",
        "category": "Disruptive",
        "feedback": "Culturally insensitive and rude. Embarrasses a global colleague publicly rather than bridging the communication gap."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Nodding along in silence when communication breaks down across regions leads directly to expensive execution errors."
      }
    ]
  },
  {
    "id": "meeting_25",
    "meetingType": "All-Hands Briefing",
    "leaderPrompt": "PRESENTER: 'The new healthcare benefits will cover...' [A hot mic from a participant erupts with a dog barking frantically, completely drowning out the speaker].",
    "options": [
      {
        "text": "@[Host Name], we have some background noise coming through. Could you use host controls to mute all participants?",
        "category": "Optimal",
        "feedback": "Quickly identifies a systemic solution (host controls) rather than yelling at individuals, restoring order instantly."
      },
      {
        "text": "Aww, whose dog is that? So cute!",
        "category": "Suboptimal",
        "feedback": "Validates the disruption. Encourages off-topic chatter while a crucial HR benefits presentation is stalled."
      },
      {
        "text": "Mute your damn mics people, this is a professional meeting!",
        "category": "Disruptive",
        "feedback": "Overreacts with anger. Escalates an accidental annoyance into a hostile and aggressive confrontation."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Bystander effect. Sitting in silence while an entire all-hands meeting is ruined by background noise is passive behavior."
      }
    ]
  },
  {
    "id": "meeting_26",
    "meetingType": "Project Leadership Clash",
    "leaderPrompt": "MANAGER A: 'We must present this as a PDF.' \nMANAGER B: 'No, an Excel model is better.' \nMANAGER A: 'Team, which format do you want to build?'",
    "options": [
      {
        "text": "What is the primary way the client intends to interact with the data? That might dictate whether we use PDF or Excel.",
        "category": "Optimal",
        "feedback": "Refuses to take sides in a political dispute, instead reframing the conflict around objective client requirements."
      },
      {
        "text": "Both formats have their merits! Happy to do either!",
        "category": "Suboptimal",
        "feedback": "People-pleasing behavior that adds zero clarity to the stalemate and forces the managers to keep arguing."
      },
      {
        "text": "You two need to figure this out between yourselves and stop wasting our time.",
        "category": "Disruptive",
        "feedback": "Openly insubordinate to leadership. Even if the managers are dysfunctional, this response is severely unprofessional."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to mediate or seek clarity leaves the team paralyzed while management bickers over deliverables."
      }
    ]
  },
  {
    "id": "meeting_27",
    "meetingType": "Peer Design Review",
    "leaderPrompt": "CREATIVE DIRECTOR: 'I don't appreciate the feedback. You guys just don't understand my vision for this brand campaign. It's staying as is.'",
    "options": [
      {
        "text": "I hear your vision. Could we map these visual choices back to the user personas the client gave us to ensure alignment?",
        "category": "Optimal",
        "feedback": "De-escalates defensive behavior by validating the creator, then gently tethering the work back to objective project constraints."
      },
      {
        "text": "It looks really pretty, great job!",
        "category": "Suboptimal",
        "feedback": "Capitulates to defensive behavior and offers false praise, destroying the purpose of a critical review session."
      },
      {
        "text": "Your vision ignores the project brief. Get over your ego and fix it.",
        "category": "Disruptive",
        "feedback": "Personal attack. Turns a professional disagreement into a deeply hostile and damaging interpersonal conflict."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Backing down in the face of defensiveness allows subpar work to go to the client, hurting the agency's reputation."
      }
    ]
  },
  {
    "id": "meeting_28",
    "meetingType": "Weekly Status Wrap-Up",
    "leaderPrompt": "TEAM LEAD: 'Okay, great discussion everyone. Lots of good ideas. Let's all just execute on what we talked about. See you next week!'",
    "options": [
      {
        "text": "Before we drop, could we get a quick recap in chat of who is specifically owning the three action items we just discussed?",
        "category": "Optimal",
        "feedback": "Catches a major meeting failure (lack of accountability) and forces clarity on ownership before participants disperse."
      },
      {
        "text": "Great meeting everyone! Have a great weekend!",
        "category": "Suboptimal",
        "feedback": "Celebrates a highly ineffective meeting where no actual tasks were assigned, contributing to organizational slowness."
      },
      {
        "text": "Typical meeting, we talked for an hour and didn't actually assign anything to anyone.",
        "category": "Disruptive",
        "feedback": "Passive-aggressive complaining. Identifies the issue but refuses to actually solve it, choosing instead to be cynical."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Leaving a meeting without clear action items guarantees that nothing will be done, necessitating another meeting later."
      }
    ]
  },
  {
    "id": "meeting_29",
    "meetingType": "Product Brainstorming",
    "leaderPrompt": "PRODUCT OWNER: '...and if we integrate AI, maybe the app could eventually predict what the user wants to eat before they even know it!'",
    "options": [
      {
        "text": "Love the long-term vision. For next week's MVP, should we focus on just getting the basic search query functioning first?",
        "category": "Optimal",
        "feedback": "Validates the brainstorming energy but firmly pulls the team back to the immediate, practical scope of the MVP."
      },
      {
        "text": "Wow, that would be so futuristic and cool!",
        "category": "Suboptimal",
        "feedback": "Feeds the scope creep. Encourages the team to waste time discussing sci-fi features instead of immediate deliverables."
      },
      {
        "text": "This is ridiculous. We can't even get the login button to work, stop daydreaming.",
        "category": "Disruptive",
        "feedback": "Aggressive and toxic. Kills team creativity and morale through heavy-handed sarcasm and cynicism."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Allowing leaders to drift into massive scope creep unchecked almost guarantees the team will miss their actual deadlines."
      }
    ]
  },
  {
    "id": "meeting_30",
    "meetingType": "Vendor Performance Review",
    "leaderPrompt": "IT DIRECTOR: 'Our hosting provider went down twice this month. I need a vote: do we give them a final warning, or terminate the contract today?'",
    "options": [
      {
        "text": "Do we have a backup provider pre-vetted, and what is the estimated downtime if we terminate and migrate today?",
        "category": "Optimal",
        "feedback": "Injects critical risk-management questions into an emotional decision, ensuring the company doesn't shoot itself in the foot."
      },
      {
        "text": "Whatever you think is best! I trust your judgment!",
        "category": "Suboptimal",
        "feedback": "Abdicates responsibility entirely. Fails to provide the requested input during a high-stakes infrastructure decision."
      },
      {
        "text": "Fire them immediately. Who hired these amateurs in the first place?",
        "category": "Disruptive",
        "feedback": "Reactionary and vindictive. Ignores the massive operational risks of suddenly terminating a core infrastructure vendor."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Remaining silent when leadership asks for input on a critical infrastructure decision shows a lack of strategic engagement."
      }
    ]
  },
  {
    "id": "meeting_31",
    "meetingType": "HR Check-in / Morale Pulse",
    "leaderPrompt": "HR MANAGER: 'We know the mandatory overtime has been tough. Can everyone let me know honestly in chat how they are managing their workload?'",
    "options": [
      {
        "text": "The overtime is manageable short-term, but we are seeing quality drop. Could we look at hiring contractors to offset the burnout?",
        "category": "Optimal",
        "feedback": "Provides honest, professional feedback that highlights business impact (quality) and suggests a viable solution (contractors)."
      },
      {
        "text": "Just grinding through it! Loving the hustle!",
        "category": "Suboptimal",
        "feedback": "Toxic productivity. Masks genuine team burnout and prevents HR from realizing the severity of the workload issue."
      },
      {
        "text": "It's basically slave labor. Half the team is updating their resumes.",
        "category": "Disruptive",
        "feedback": "Highly unprofessional and dramatic. Threatening mutiny shuts down constructive dialogue with HR."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Withholding feedback during a pulse check guarantees that management will assume everything is fine and change nothing."
      }
    ]
  },
  {
    "id": "meeting_32",
    "meetingType": "Sprint Planning Conflict",
    "leaderPrompt": "PRODUCT MANAGER: 'Engineering says we should spend the sprint fixing tech debt, but Sales wants the new features. What's the priority?'",
    "options": [
      {
        "text": "Can engineering quantify the risk of a system crash if we build new features on top of the current tech debt?",
        "category": "Optimal",
        "feedback": "Bridges the gap between technical reality and business goals by forcing a conversation about quantifiable risk."
      },
      {
        "text": "I think we can do both if we just work hard enough!",
        "category": "Suboptimal",
        "feedback": "Dangerously naive. Suggesting the team can simply 'work harder' to do two conflicting things guarantees failure."
      },
      {
        "text": "Sales always dictates our sprints. We might as well stop pretending Engineering has a say.",
        "category": "Disruptive",
        "feedback": "Extremely cynical and defeated. Spreads resentment and tribalism between departments."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to help mediate between Engineering and Sales leads to poorly planned sprints and internal resentment."
      }
    ]
  },
  {
    "id": "meeting_33",
    "meetingType": "Pre-Pitch Huddle",
    "leaderPrompt": "VP OF SALES: 'I haven't had time to look at the slide deck you built, so I'm just going to wing the presentation to the client in 10 minutes.'",
    "options": [
      {
        "text": "Would you like to take 5 minutes now to review the key transitions, or should I jump in during the pricing slide to help out?",
        "category": "Optimal",
        "feedback": "Manages up perfectly. Offers a quick prep life-raft and volunteers to cover specific sections to protect the pitch."
      },
      {
        "text": "You always wing it perfectly! You'll do great!",
        "category": "Suboptimal",
        "feedback": "Enables severe lack of preparation, increasing the likelihood that the pitch will fail and the client will be unimpressed."
      },
      {
        "text": "That's completely disrespectful to the work we put into the deck. I'm not attending if you're winging it.",
        "category": "Disruptive",
        "feedback": "Insubordinate and hostile. Walking out 10 minutes before a pitch over a bruised ego is career suicide."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Watching a superior walk blindly into a major client presentation without offering assistance shows poor team support."
      }
    ]
  },
  {
    "id": "meeting_34",
    "meetingType": "Impromptu Sync",
    "leaderPrompt": "MANAGER: 'Hey everyone, I pulled you all onto this call to just... sync on things. Does anyone have anything they want to bring up?'",
    "options": [
      {
        "text": "Since we don't have a formal agenda, could we use this time to quickly clear the backlog of pending design approvals?",
        "category": "Optimal",
        "feedback": "Takes charge of a directionless meeting and pivots the team toward a productive, tangible outcome."
      },
      {
        "text": "Just happy to be here catching up with everyone!",
        "category": "Suboptimal",
        "feedback": "Encourages time-wasting. Validates the existence of a meeting that has no actual business purpose."
      },
      {
        "text": "If there's no agenda, I'm leaving. I have actual work to do.",
        "category": "Disruptive",
        "feedback": "Blunt and aggressive. While the sentiment is understandable, the execution is profoundly unprofessional and burns bridges."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Sitting silently in a purposeless meeting is a passive acceptance of wasted company time and poor management."
      }
    ]
  },
  {
    "id": "meeting_35",
    "meetingType": "Emergency PR Crisis",
    "leaderPrompt": "PR DIRECTOR: 'A negative news article just leaked about our data practices. I need everyone's immediate thoughts on whether we release a statement or wait it out.'",
    "options": [
      {
        "text": "Before we speak, has Legal reviewed the specific claims in the article to determine our actual liability?",
        "category": "Optimal",
        "feedback": "Outstanding crisis management. Injects essential risk-mitigation and cross-departmental protocol (Legal) into a panicked situation."
      },
      {
        "text": "Oh no, this is terrible! Let's hope it blows over soon!",
        "category": "Suboptimal",
        "feedback": "Empty commiseration that adds zero strategic value during a fast-moving public relations crisis."
      },
      {
        "text": "I knew our data policies were shady. We deserve this backlash.",
        "category": "Disruptive",
        "feedback": "Incredibly damaging and unprofessional. Broadcasting internal accusations during a crisis is grounds for termination."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to contribute during an all-hands crisis when explicitly asked for strategic input shows a severe lack of leadership."
      }
    ]
  },
  {
    "id": "meeting_36",
    "meetingType": "Quarterly OKR Review",
    "leaderPrompt": "TEAM LEAD: 'We missed our Q2 lead generation target by 40%. The floor is open—why did this happen and how do we fix it?'",
    "options": [
      {
        "text": "I noticed our primary ad channel changed algorithms in May. Should we shift Q3 budget toward organic content to compensate?",
        "category": "Optimal",
        "feedback": "Takes accountability by identifying a specific root cause and immediately proposing a forward-looking operational solution."
      },
      {
        "text": "We will definitely try harder next quarter! Go team!",
        "category": "Suboptimal",
        "feedback": "Toxic positivity. Completely ignores the massive 40% failure and fails to address the 'why' or 'how to fix it' prompt."
      },
      {
        "text": "It's because the marketing team's messaging was terrible. Sales can't close bad leads.",
        "category": "Disruptive",
        "feedback": "Defensive finger-pointing. Destroys cross-functional trust and turns a problem-solving session into a turf war."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Remaining silent after a massive team failure demonstrates a lack of accountability and strategic engagement."
      }
    ]
  },
  {
    "id": "meeting_37",
    "meetingType": "New Hire Onboarding",
    "leaderPrompt": "HR MANAGER: '...so those are the basics of our PTO policy. Any questions from the new folks before we move on to IT security?'",
    "options": [
      {
        "text": "Just to clarify, does the rollover limit for PTO apply at the calendar year-end or on our individual work anniversaries?",
        "category": "Optimal",
        "feedback": "Asks a highly specific, universally useful clarifying question that prevents future HR confusion for the whole cohort."
      },
      {
        "text": "Wow, so generous! Can't wait to take a vacation!",
        "category": "Suboptimal",
        "feedback": "Unnecessary filler that clutters the chat and wastes time during a dense onboarding schedule."
      },
      {
        "text": "Three weeks is pretty low for this industry. Are we allowed to negotiate for more?",
        "category": "Disruptive",
        "feedback": "Highly inappropriate timing. Publicly complaining about benefits during a group onboarding creates an awkward, negative atmosphere."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "While acceptable if you genuinely have no questions, silence is a missed opportunity to demonstrate engagement early on."
      }
    ]
  },
  {
    "id": "meeting_38",
    "meetingType": "Executive Strategy Sync",
    "leaderPrompt": "CEO: 'I'm thinking we pivot the entire product roadmap to focus on Blockchain technology. It's the future. Thoughts?'",
    "options": [
      {
        "text": "Interesting direction. Could we assign a small tiger team to research the potential ROI and engineering cost before we fully pivot?",
        "category": "Optimal",
        "feedback": "Expertly handles 'executive shiny object syndrome' by proposing a contained, data-driven validation phase before committing resources."
      },
      {
        "text": "Brilliant idea! Blockchain is definitely the future!",
        "category": "Suboptimal",
        "feedback": "Sycophantic enabling. Agrees to a massive, highly risky strategic pivot without requesting any data or validation."
      },
      {
        "text": "Blockchain is a dying fad. Pivoting now would be a massive, embarrassing mistake.",
        "category": "Disruptive",
        "feedback": "Blunt and condescending. Publicly insulting the CEO's idea is a fast track to career derailment, regardless of whether you are right."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to challenge or vet a potentially disastrous executive pivot makes you complicit in the company's strategic failure."
      }
    ]
  },
  {
    "id": "meeting_39",
    "meetingType": "Cross-Functional Kickoff",
    "leaderPrompt": "PROJECT MANAGER: 'Alright, we have 45 minutes to get through this 80-slide deck. Please hold all questions until the end.'",
    "options": [
      {
        "text": "Understood. I'll drop any critical questions in the chat as we go so they are queued up for the end.",
        "category": "Optimal",
        "feedback": "Respects the PM's rule while establishing an efficient asynchronous way to capture questions before they are forgotten."
      },
      {
        "text": "Ready when you are! Let's power through it!",
        "category": "Suboptimal",
        "feedback": "Adds unnecessary noise to a meeting that is already under extreme time pressure."
      },
      {
        "text": "Actually, I have a question right now about slide 2 that can't wait.",
        "category": "Disruptive",
        "feedback": "Blatant disregard for the meeting rules. Disrupts the flow and undermines the Project Manager's authority."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "While technically following the rule, failing to acknowledge the directive or suggest a queueing system is a missed opportunity for efficiency."
      }
    ]
  },
  {
    "id": "meeting_40",
    "meetingType": "Post-Launch Retrospective",
    "leaderPrompt": "DEVELOPER: 'The launch was a nightmare because Marketing changed the promotional dates without telling us, so the servers crashed under the load.'",
    "options": [
      {
        "text": "Going forward, should we implement a mandatory sign-off process between Marketing and Engineering for all promotional timelines?",
        "category": "Optimal",
        "feedback": "Defuses the inter-departmental hostility by immediately pivoting the focus to a systemic, preventative process improvement."
      },
      {
        "text": "It was a crazy day for sure! But we survived!",
        "category": "Suboptimal",
        "feedback": "Dismissive toxic positivity that fails to address the root cause of a major inter-departmental failure."
      },
      {
        "text": "Marketing always does this. They have no respect for the technical infrastructure.",
        "category": "Disruptive",
        "feedback": "Piles onto the blame game, increasing hostility and ensuring the post-mortem devolves into a venting session rather than a learning opportunity."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Remaining silent while departments attack each other allows the conflict to escalate and prevents any actual process improvement."
      }
    ]
  },
  {
    "id": "meeting_41",
    "meetingType": "Sales Pipeline Review",
    "leaderPrompt": "VP OF SALES: 'Our win rate is down 15%. I need every Account Executive to type one reason in the chat why deals are stalling.'",
    "options": [
      {
        "text": "Prospects are pushing back on the new pricing tier; we may need more collateral proving the ROI of the added features.",
        "category": "Optimal",
        "feedback": "Provides specific, actionable feedback from the front lines and suggests a concrete marketing solution to help close deals."
      },
      {
        "text": "It's just a slow month, but we will bounce back!",
        "category": "Suboptimal",
        "feedback": "Fails to provide the requested data, offering vague optimism instead of analyzing why the win rate is actually dropping."
      },
      {
        "text": "Because the product team keeps releasing buggy updates and we look stupid trying to sell it.",
        "category": "Disruptive",
        "feedback": "Uses the prompt as an excuse to launch a hostile attack on another department, creating a toxic blame culture."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Ignoring a direct request from a VP to provide crucial pipeline feedback is negligent and severely limits your visibility."
      }
    ]
  },
  {
    "id": "meeting_42",
    "meetingType": "External Partner Sync",
    "leaderPrompt": "PARTNER: 'We're really unhappy with the current API integration. It's clunky. We need your team to completely rebuild it by next week.'",
    "options": [
      {
        "text": "A full rebuild by next week is outside our current scope, but could we schedule a deep-dive tomorrow to identify the most critical pain points?",
        "category": "Optimal",
        "feedback": "Sets a firm boundary on an unreasonable timeline while immediately offering a constructive, collaborative next step to address their frustration."
      },
      {
        "text": "We will absolutely do our best to get that sorted out for you!",
        "category": "Suboptimal",
        "feedback": "Makes a vague, dangerous promise that the partner will interpret as agreement to an impossible, out-of-scope demand."
      },
      {
        "text": "That timeline is impossible and frankly, the integration works fine on our end.",
        "category": "Disruptive",
        "feedback": "Combative and dismissive. Instantly escalates the partner's frustration into a full-blown contract dispute."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Silence equals agreement in partner negotiations. Failing to push back guarantees your engineering team will face an impossible deadline."
      }
    ]
  },
  {
    "id": "meeting_43",
    "meetingType": "Weekly Department Sync",
    "leaderPrompt": "MANAGER: [Has been speaking on mute for 45 seconds while presenting a slide].",
    "options": [
      {
        "text": "@[Manager Name], you're currently on mute! We missed the context for this slide.",
        "category": "Optimal",
        "feedback": "Promptly and politely halts the presentation to fix the technical issue before more information is lost."
      },
      {
        "text": "Great slide, really powerful graphics!",
        "category": "Suboptimal",
        "feedback": "Bizarrely praises the visual while ignoring the fact that the presenter is completely inaudible."
      },
      {
        "text": "You're on mute. Do we have to go through this every single week?",
        "category": "Disruptive",
        "feedback": "Highly disrespectful and exasperated tone directed at management over a common technical error."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Bystander effect. Letting a manager speak on mute for an extended period creates intense awkwardness and wastes time."
      }
    ]
  },
  {
    "id": "meeting_44",
    "meetingType": "Compliance Audit Briefing",
    "leaderPrompt": "COMPLIANCE OFFICER: 'If we fail this audit, we face severe fines. Everyone must complete the mandatory data training by Friday. Confirm in chat.'",
    "options": [
      {
        "text": "Confirmed. Will the training platform automatically log our completion, or do we need to send you our certificates manually?",
        "category": "Optimal",
        "feedback": "Provides mandatory confirmation while asking a logistical question that streamlines the tracking process for the compliance team."
      },
      {
        "text": "Will do! Thanks for the heads up!",
        "category": "Suboptimal",
        "feedback": "Fails to provide the explicit, unambiguous 'Confirmed' required for a legal compliance paper trail."
      },
      {
        "text": "Another mandatory training? We barely have time to do our actual jobs.",
        "category": "Disruptive",
        "feedback": "Insubordinate complaining about legally required training sets a toxic precedent and puts the company at risk."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to confirm receipt of critical compliance directives exposes both you and the company to severe liability."
      }
    ]
  },
  {
    "id": "meeting_45",
    "meetingType": "Brainstorming Session",
    "leaderPrompt": "COLLEAGUE A: 'I think we should use a blue color palette.' \nCOLLEAGUE B: 'No, red is much more aggressive and modern.' \n[Argument continues for 5 minutes without resolution].",
    "options": [
      {
        "text": "Since we're stuck, should we A/B test both palettes with a small user segment and let the data decide?",
        "category": "Optimal",
        "feedback": "Excellent mediation. Bypasses subjective arguing by proposing an objective, data-driven methodology to solve the dispute."
      },
      {
        "text": "They both sound like great options!",
        "category": "Suboptimal",
        "feedback": "People-pleasing that adds no value and allows the circular argument to continue wasting the meeting's time."
      },
      {
        "text": "This argument is pointless and you are both wasting everyone's time. Move on.",
        "category": "Disruptive",
        "feedback": "While factually true, the aggressive, policing tone creates hostility and makes you look arrogant rather than helpful."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Passively allowing two colleagues to hijack a meeting with a subjective argument drains productivity from everyone else."
      }
    ]
  },
  {
    "id": "meeting_46",
    "meetingType": "Annual Goal Setting",
    "leaderPrompt": "DIRECTOR: 'I want this team to increase output by 50% next year without any additional headcount. Can we do it?'",
    "options": [
      {
        "text": "To achieve a 50% increase without new headcount, which legacy projects can we deprecate to free up our capacity?",
        "category": "Optimal",
        "feedback": "Diplomatically challenges an unrealistic expectation by forcing a conversation about necessary operational trade-offs."
      },
      {
        "text": "We love a challenge! We will give it 110%!",
        "category": "Suboptimal",
        "feedback": "Toxic positivity. Committing the team to an impossible goal without securing resources or adjusting scope guarantees burnout."
      },
      {
        "text": "That's completely delusional. We are already at max capacity.",
        "category": "Disruptive",
        "feedback": "Insubordinate and hostile. Attacking the Director's judgment publicly destroys professional relationships."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Remaining silent implies consent to an impossible workload, setting the entire team up for inevitable failure and burnout."
      }
    ]
  },
  {
    "id": "meeting_47",
    "meetingType": "Project Post-Mortem",
    "leaderPrompt": "PROJECT MANAGER: 'The client was furious that the deliverables were late. Where did the bottleneck happen?'",
    "options": [
      {
        "text": "The bottleneck occurred in the final QA phase. We need to build in a buffer week for testing on future timelines to prevent this.",
        "category": "Optimal",
        "feedback": "Identifies the failure objectively without assigning personal blame, and immediately suggests a concrete process improvement."
      },
      {
        "text": "Mistakes happen, we will just do better next time!",
        "category": "Suboptimal",
        "feedback": "Platitudes offer zero value in a post-mortem, preventing the team from identifying the actual root cause of the failure."
      },
      {
        "text": "It was late because [Colleague Name] took three days to answer a simple email. It's their fault.",
        "category": "Disruptive",
        "feedback": "Publicly throwing a specific colleague under the bus destroys psychological safety and teamwork."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Refusing to contribute to a post-mortem allows failed processes to remain in place, ensuring future projects will also fail."
      }
    ]
  },
  {
    "id": "meeting_48",
    "meetingType": "Virtual Town Hall",
    "leaderPrompt": "CEO: 'Before we close out, I want to take a moment to acknowledge the tragic news event that happened this morning...'",
    "options": [
      {
        "text": "(Stay Silent)",
        "category": "Optimal",
        "feedback": "Displays high emotional intelligence. Recognizing when a somber or serious moment requires respectful silence rather than chat input."
      },
      {
        "text": "So sad! Sending thoughts and prayers!",
        "category": "Suboptimal",
        "feedback": "While well-intentioned, filling the chat with performative condolences during a serious leadership address can feel disruptive and cluttered."
      },
      {
        "text": "Can we skip this and get back to the quarterly numbers?",
        "category": "Disruptive",
        "feedback": "Profoundly tone-deaf and sociopathic. Shows a total lack of empathy and basic human decency in a corporate setting."
      },
      {
        "text": "I heard it was actually a conspiracy, check out this link...",
        "category": "Silent Failure",
        "feedback": "Using a somber corporate moment to post controversial or off-topic links is highly unprofessional and inappropriate."
      }
    ]
  },
  {
    "id": "meeting_49",
    "meetingType": "Design Sprint Review",
    "leaderPrompt": "LEAD DESIGNER: 'So as you can see, we changed the entire UI to feature a dark mode theme because it looks cooler.'",
    "options": [
      {
        "text": "Looks sleek. Has this dark mode been tested for accessibility and contrast compliance for our visually impaired users?",
        "category": "Optimal",
        "feedback": "Validates the aesthetic choice while immediately checking for critical, legally required accessibility standards."
      },
      {
        "text": "Dark mode is the best! Looks so awesome!",
        "category": "Suboptimal",
        "feedback": "Superficial praise that ignores potential functional or accessibility issues with a major UI overhaul."
      },
      {
        "text": "Dark mode is a terrible idea for our demographic. Change it back.",
        "category": "Disruptive",
        "feedback": "Dictatorial and combative. Rejects the work outright without offering data or constructive reasoning."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Failing to ask about accessibility or user impact during a major design overhaul makes you complicit in potential usability failures."
      }
    ]
  },
  {
    "id": "meeting_50",
    "meetingType": "Final Vendor Selection",
    "leaderPrompt": "PROCUREMENT: 'We have to choose between Vendor A, who is cheaper but slower, or Vendor B, who is faster but 20% more expensive. I need a final vote in chat.'",
    "options": [
      {
        "text": "Vote: Vendor B. The 20% premium is justified because the faster delivery allows us to hit the lucrative Q3 market window.",
        "category": "Optimal",
        "feedback": "Provides a clear vote and brilliantly justifies it using strategic business context (Q3 market window)."
      },
      {
        "text": "Either one works for me, whatever the team decides!",
        "category": "Suboptimal",
        "feedback": "Abdicates responsibility during a critical financial decision. Fails to provide the requested vote."
      },
      {
        "text": "Both vendors are terrible, we should just build it in-house.",
        "category": "Disruptive",
        "feedback": "Introduces a massive, unvetted alternative at the very last second, completely derailing the final selection process."
      },
      {
        "text": "(Stay Silent)",
        "category": "Silent Failure",
        "feedback": "Remaining silent when a final vote is required stalls the procurement process and shows a lack of decisiveness."
      }
    ]
  }
];